import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Divider,
  Paper,
  Tooltip,
  useTheme,
  alpha,
  InputAdornment,
  CircularProgress,
  Grid,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import axios from "axios";
import useUserFilters from "../../hooks/useUserFilters";
import useDraggable from "../../hooks/useDraggable";
import { getBookieLogoUrl } from "../../utils/common";

const BookieWeightsModal = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [bookies, setBookies] = useState({});
  const [bookieWeights, setBookieWeights] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingWeights, setSavingWeights] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredBookies, setFilteredBookies] = useState({});
  const [logoFailed, setLogoFailed] = useState({});
  const { filters, fetchFilters, saveFilter } = useUserFilters();

  // Draggable functionality for desktop
  const { handleMouseDown, getDialogStyle, getDragHandleStyle, isDragging } =
    useDraggable(isDesktop, open, "bookie_weights");

  const fetchBookies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://tool.bonusbank.com.au/api/v3/bookmakers/?core=true&os_bookies=true"
      );
      if (response.data && response.data.bookies) {
        setBookies(response.data.bookies);
        setFilteredBookies(response.data.bookies);
      }
    } catch (error) {
      console.error("Error fetching bookies:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSavedWeights = useCallback(() => {
    fetchFilters("value-betting", "bookieWeightConfig");
  }, [fetchFilters]);

  useEffect(() => {
    if (open) {
      fetchBookies();
      fetchSavedWeights();
    }
  }, [open, fetchBookies, fetchSavedWeights]);

  useEffect(() => {
    if (filters && filters.content) {
      setBookieWeights(filters.content);
    }
  }, [filters]);

  useEffect(() => {
    if (Object.keys(bookies).length === 0) return;

    if (searchTerm.trim() === "") {
      setFilteredBookies(bookies);
    } else {
      const filtered = Object.entries(bookies).reduce((acc, [name, data]) => {
        if (name.toLowerCase().includes(searchTerm.toLowerCase())) {
          acc[name] = data;
        }
        return acc;
      }, {});
      setFilteredBookies(filtered);
    }
  }, [searchTerm, bookies]);

  const handleWeightChange = (bookieName, value) => {
    setBookieWeights((prev) => ({
      ...prev,
      [bookieName]: value,
    }));
  };

  const resetBookieWeight = (bookieName) => {
    setBookieWeights((prev) => {
      const newWeights = { ...prev };
      delete newWeights[bookieName];
      return newWeights;
    });
  };

  const resetAllWeights = () => {
    setBookieWeights({});
  };

  const setAllWeightsToZero = () => {
    const zeroWeights = Object.keys(bookies).reduce((acc, bookieName) => {
      acc[bookieName] = 0;
      return acc;
    }, {});
    setBookieWeights(zeroWeights);
  };

  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      const nonDefaultWeights = Object.entries(bookieWeights).reduce(
        (acc, [bookieName, weight]) => {
          if (weight !== 1) {
            acc[bookieName] = weight;
          }
          return acc;
        },
        {}
      );

      await saveFilter({
        key: "bookieWeightConfig",
        content: nonDefaultWeights,
        category: "value-betting",
      });

      if (onSave) {
        onSave(nonDefaultWeights);
      }

      onClose();
    } catch (error) {
      console.error("Error saving bookie weights:", error);
    } finally {
      setSavingWeights(false);
    }
  };

  const getWeightsString = useCallback(() => {
    return Object.entries(bookieWeights)
      .filter(([_, weight]) => weight !== 1)
      .map(([bookieName, weight]) => `${bookieName}:${weight}`)
      .join(",");
  }, [bookieWeights]);

  const sortedBookies = Object.entries(filteredBookies).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const getBookieWeight = (bookieName) => {
    return bookieWeights[bookieName] !== undefined
      ? bookieWeights[bookieName]
      : 1;
  };

  const customWeightsCount = useMemo(() => {
    return Object.keys(bookieWeights).length;
  }, [bookieWeights]);

  const getWeightColor = (weight) => {
    if (weight === 0) return theme.palette.error.main;
    if (weight > 2) return theme.palette.success.main;
    return theme.palette.primary.main;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10],
          overflow: "hidden",
          bgcolor: theme.palette.background.default,
          ...getDialogStyle(),
          transition: isDragging ? "none" : "transform 0.1s ease-out",
        },
      }}
      scroll="paper"
    >
      <DialogTitle
        onMouseDown={handleMouseDown}
        sx={{
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ...getDragHandleStyle(),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isDesktop && (
            <DragIndicatorIcon
              sx={{
                fontSize: 20,
                color: theme.palette.text.disabled,
                opacity: 0.6,
              }}
            />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bookie Weights Configuration
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <InfoIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Set weights (0-3) for each bookmaker. Default is 1. Higher weights increase influence, lower weights decrease it. Zero excludes the bookmaker.
            </Typography>
          </Box>

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search bookmakers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("") }>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: "100%", mb: 2 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 900, overflowY: "auto", pr: 1 }}>
            <Grid container spacing={1.5}>
              {sortedBookies.map(([bookieName, data]) => {
                const weight = getBookieWeight(bookieName);
                return (
                  <Grid item xs={12} md={4} key={bookieName}>
                    <Paper
                      elevation={0}
                      sx={{
                        py: 1,
                        px: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : theme.palette.background.paper,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Tooltip title={bookieName}>
                          <Box
                            sx={{
                              width: 110,
                              height: 35,
                              display: "flex",
                              alignItems: "center",
                              mr: 1.5,
                            }}
                          >
                            {!logoFailed[bookieName] ? (
                              <Box
                                component="img"
                                src={getBookieLogoUrl(bookieName)}
                                alt={bookieName}
                                onError={() =>
                                  setLogoFailed((prev) => ({ ...prev, [bookieName]: true }))
                                }
                                sx={{
                                  maxHeight: "35px",
                                  maxWidth: "110px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {bookieName}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                        
                        <ToggleButtonGroup
                          value={weight.toString()}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) {
                              handleWeightChange(bookieName, parseInt(newValue, 10));
                            }
                          }}
                          aria-label="bookie weight"
                          size="small"
                          sx={{ 
                            ml: 'auto',
                            '& .MuiToggleButtonGroup-grouped': {
                              border: 1,
                              borderColor: 'divider',
                              m: 0,
                              '&.Mui-selected': {
                                borderColor: (theme) => getWeightColor(weight),
                                backgroundColor: (theme) => alpha(getWeightColor(weight), 0.1),
                                color: (theme) => getWeightColor(weight),
                                fontWeight: 'bold',
                              },
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                              },
                              '&:first-of-type': {
                                borderTopLeftRadius: '4px',
                                borderBottomLeftRadius: '4px',
                              },
                              '&:last-of-type': {
                                borderTopRightRadius: '4px',
                                borderBottomRightRadius: '4px',
                              },
                            }
                          }}
                        >
                          {[0, 1, 2, 3].map((buttonValue) => (
                            <ToggleButton 
                              key={buttonValue} 
                              value={buttonValue.toString()}
                              sx={{ 
                                width: '36px', 
                                height: '32px',
                                padding: 0,
                                fontSize: '0.85rem',
                                color: buttonValue === weight ? getWeightColor(buttonValue) : 'inherit'
                              }}
                            >
                              {buttonValue}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions 
        sx={{ 
          px: 2, 
          py: 1.5, 
          backgroundColor: theme.palette.background.paper,
          justifyContent: "space-between" 
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={setAllWeightsToZero}
            startIcon={<BlockIcon />}
            disabled={Object.keys(bookies).length === 0}
            size="small"
            sx={{
              color: theme.palette.error.main,
              borderColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                borderColor: theme.palette.error.main,
              }
            }}
            variant="outlined"
          >
            ZERO ALL
          </Button>
          <Button
            onClick={resetAllWeights}
            startIcon={<RestoreIcon />}
            disabled={Object.keys(bookieWeights).length === 0}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            RESET ALL
          </Button>
        </Box>
        <Box>
          <Button 
            size="small" 
            onClick={onClose} 
            sx={{ 
              mr: 1, 
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={saveWeights}
            variant="contained"
            color="primary"
            disabled={savingWeights}
            startIcon={savingWeights ? <CircularProgress size={16} /> : <SaveIcon />}
            size="small"
            sx={{ 
              fontWeight: 500,
              px: 2
            }}
          >
            SAVE WEIGHTS
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BookieWeightsModal;
